/**
 * ENHANCED EFFECTS' register of numbers: every tunable the particle look is made
 * of, the pool ceilings, and the two size laws.
 *
 * Split out of `particle-effects.ts` when that file passed aislop's 400-line
 * ceiling, along the seam the module already had: this file is **what the look
 * is worth**, `particle-effects.ts` next door is **how it is thrown**. The
 * provenance of every number stays with the number, which is the whole reason a
 * register exists rather than literals in spawn calls.
 *
 * `particle-effects.ts` re-exports all of it, so no consumer moved.
 */

import { PUFF_LIFE_SECONDS, PUFF_RISE_MPS } from "./smoke-trail.ts";

/**
 * Every number the look is made of, in one register — the shape
 * `smoke-trail.ts`'s `SMOKE_TUNABLES` and `effect-sprites.ts`'s
 * `EFFECT_TUNABLES` use, so a guess stays an enumerable work item instead of a
 * literal buried in a spawn call.
 */
export const PARTICLE_TUNABLES = {
  /** Seconds a flame particle lives. Short: a flame licks off and is gone. */
  fireLifeSeconds: 0.45,
  /** Flame particles per second, per burning aeroplane. */
  fireRatePerSecond: 50,
  /**
   * Metres a flame is born at, and metres per second it swells by as it cools.
   *
   * CALIBRATED against the picture the toggle's *off* state draws, on a real
   * GPU at 1280x800 (2026-08-23): the sprite fire and its `SMOKE.BMP` cluster
   * span roughly two metres across the airframe from the chase camera, and the
   * first pass at these numbers — a flame swelling to 1.9 m and a puff to 6.7 m
   * — put a thirteen-metre grey ball over the whole aeroplane. A world-sized
   * particle is not a screen-sized sprite: the original's puff holds its pixel
   * size at every range because a blitter has no other option, and copying its
   * *apparent* size at one range into a world radius is how a trail ends up
   * swallowing the thing it belongs to.
   */
  fireRadiusM: 0.45,
  fireGrowthMps: 1.1,
  /**
   * Fraction of the airframe's velocity a flame is born with. Under 1 on
   * purpose — the deficit is what makes the flame stream *backwards* over the
   * fuselage instead of flying in formation with it.
   */
  fireVelocityInheritance: 0.35,
  /** Metres per second of random spread on a flame's birth velocity. */
  fireJitterMps: 1.2,
  /** Seconds a puff lives. RECOVERED: `SMOKE.DAT[1]`, via `smoke-trail.ts`. */
  smokeLifeSeconds: PUFF_LIFE_SECONDS,
  /**
   * Puff particles per second, per trailing aeroplane. A port extension and
   * the one number that is *deliberately* not the original's: `SMOKE.DAT[0]`
   * gives 2.38 puffs a second, which at 60 m/s leaves 25 m between them. A
   * particle trail resolves the same 2.52 s of smoke into many more, smaller
   * pieces — that is what makes it a particle trail — so the rate is raised
   * and the radius dropped to fill the same volume.
   */
  smokeRatePerSecond: 42,
  /**
   * Metres a puff is born at, and metres per second it billows by — the same
   * calibration `fireRadiusM` records. At 42 puffs a second behind an aeroplane
   * doing 50 m/s these sit 1.2 m apart and grow to 2.3 m, which overlaps into a
   * continuous rope of smoke. At 26/s and a 1.8 m end radius — the first pass —
   * they did not quite touch, and a trail that reads as a string of separate
   * balloons is the one failure mode a *particle* trail has no excuse for.
   */
  smokeRadiusM: 0.55,
  smokeGrowthMps: 0.7,
  /** Fraction of the airframe's velocity a puff is born with — nearly none, so the trail hangs where it was made. */
  smokeVelocityInheritance: 0.12,
  /** Metres per second of random spread on a puff's birth velocity. */
  smokeJitterMps: 1.1,
  /** Metres per second a puff settles at. RECOVERED-adjacent: `smoke-trail.ts`'s `PUFF_RISE_MPS`, reached through drag rather than applied flat. */
  smokeRiseMps: PUFF_RISE_MPS,
  /** Sparks per round that lands. Subtle by intent — this is a bullet strike, not an explosion. */
  sparksPerHit: 6,
  /** Seconds a spark lives. */
  sparkLifeSeconds: 0.26,
  /**
   * Metres a spark is born at. CALIBRATED (2026-08-23): at 0.16 m, under the
   * squared fire fade and against `EXPLO.BMP`'s dark reds, a burst counted
   * *zero* pixels from the chase camera — it was there and nobody could see it.
   * At 0.28 m it was a shower of yellow tennis balls. 0.20 m is a fleck: about
   * nineteen device pixels at chase range, bright for an instant, gone.
   */
  sparkRadiusM: 0.2,
  /** Metres of scatter around the airframe a spark is born within — see the header on why this is a scatter and not a point. */
  sparkScatterM: 1.7,
  /** Metres per second a spark is thrown at. */
  sparkSpeedMps: 14,
  /** How many hits' worth of sparks one frame will pay for, so a burst of fire cannot empty the cloud in one go. */
  maxSparkHitsPerFrame: 3,
} as const;

export type ParticleTunableKey = keyof typeof PARTICLE_TUNABLES;

/** The two taken from the original's own data rather than chosen (both via `smoke-trail.ts`). */
export const PARTICLE_RECOVERED_TUNABLES: readonly ParticleTunableKey[] = ["smokeLifeSeconds", "smokeRiseMps"];

/** Whatever is left. Derived, so a new key cannot escape the queue by being forgotten. */
export const PARTICLE_CALIBRATION_PENDING: readonly ParticleTunableKey[] = (
  Object.keys(PARTICLE_TUNABLES) as ParticleTunableKey[]
).filter((key) => !PARTICLE_RECOVERED_TUNABLES.includes(key));

/**
 * `smoke.c` allocates `SMOKE.DAT[7]` = **4** simultaneous trails, so four
 * burning aeroplanes is the original's own ceiling and the budget below is
 * sized to honour it rather than to a round number: 42 puffs a second over a
 * 2.52 s life is 106 live per trail, four trails is 424.
 *
 * **Raised from 480 for AD-018's burning buildings**, which are the port's own
 * addition and so pay for their own headroom rather than eating the four trails
 * the original's allocation buys: `MAX_SCENERY_FIRES` = 6 columns at 0.8 × 42/s
 * over the same 2.52 s life is 508 more, and 424 + 508 rounds to 960. The
 * ceiling costs capacity in one `InstancedMesh` and nothing per frame — a cloud
 * draws `liveCount` instances, not its capacity — and over-subscribing it would
 * not fail loudly, it would quietly shorten every trail in the sky, which is
 * the failure mode worth spending memory to avoid.
 *
 * **Raised again from 960 for AD-014c's crash sites**, on the same terms and by
 * the same arithmetic: `MAX_CRASH_SITES` = 6 columns at `CRASH_PLUME_INTENSITY`
 * (0.6) × 42/s over the same 2.52 s life is 381 more, and 960 + 381 rounds to
 * 1360. Six wrecks burning at once needs six aeroplanes to have reached the
 * ground inside `CRASH_TUNABLES.smokeSeconds`, which is a cap rather than a
 * forecast — but sizing to the forecast is what shortens the four trails the
 * original's own allocation buys.
 */
export const MAX_SMOKE_PARTICLES = 1360;
/**
 * Four flames at 50/s over 0.45 s is 90; the remainder is spark headroom (three
 * hits a frame at seven sparks, living 0.26 s, is ~60 in the worst case) — plus,
 * since AD-018, six burning buildings at the same rate, which is 135 more, and
 * since AD-014c six burning wrecks on the ground, which is 135 more again.
 */
export const MAX_FIRE_PARTICLES = 460;
/**
 * Sources per frame — the roster ceiling, not a budget — plus headroom for the
 * trains appended after them, the six burning buildings appended after those
 * (AD-018) and the six crash sites after those (AD-014c). Nine, two, six and
 * six is 23, so 24 still holds all four lists with the caps every one of them
 * carries.
 */
export const MAX_PARTICLE_SOURCES = 24;
/**
 * Metres of birth scatter around the anchor, at `spreadScale` 1 — the numbers
 * that were spelled inline until AD-018 needed a source wider than an
 * aeroplane. Unchanged in value, so an airframe's flame and trail are the same
 * shape they were.
 */
export const FIRE_SPREAD_M = 0.35;
export const SMOKE_SPREAD_M = 0.55;

/**
 * How much bigger a *particle* is when its source is bigger than an aeroplane.
 *
 * Sub-linear on purpose, and measured rather than picked. At scale 1 — every
 * airframe, every train — both of these are the identity, so nothing that
 * existed before AD-018 changes by a pixel. At a building's own half-extent
 * (9-31 m across the shipped corpus) a *linear* map would put a thirty-metre
 * fireball on a cottage, which is the same mistake `fireRadiusM`'s own note
 * records from the other direction.
 *
 * The two numbers differ because two capture passes over the Somme village
 * said they had to (2026-08-24, real GPU at 1280x800):
 *
 * - at the aeroplane's own 0.45 m a flame is **sub-pixel at a kilometre** — the
 *   first pass counted *zero* fire-ramp pixels while the smoke columns were
 *   plainly visible, and a burning village nobody can see is not one;
 * - at the same excess the *smoke* became a fog bank: 10 000 smoke pixels
 *   against 15 of fire, the column swallowing the fire it was supposed to be
 *   rising off. Smoke gets a third of the flame's excess and pays for the rest
 *   in `SCENERY_PLUME_INTENSITY`.
 */
export const FIRE_SIZE_EXCESS = 0.3;
export const SMOKE_SIZE_EXCESS = 0.12;

export function sizeFor(base: number, excess: number, spreadScale: number | undefined): number {
  return base * (1 + (Math.max(1, spreadScale ?? 1) - 1) * excess);
}
